import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import { DEFAULT_LIMIT, DEFAULT_SKIP, LIMIT } from '../../../consts/table';
import { Count } from '../../../models/count';

export type SchoolInviteFolder = 'inbox' | 'outbox' | 'archive';

export function searchAdminSchoolInvites(
  user: AppUser,
  folder: SchoolInviteFolder,
  options: {
    page: number;
    schoolQuery?: string;
    dateFrom?: string;
    dateTo?: string;
    kind?: string;
    sortField?: string;
    sortDirection?: string;
  }
) {
  const { roleSessionKey: key, activeSchoolId } = user;
  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/invites/search`, {
      headers: { usid: key },
      params: {
        folder,
        page: options.page,
        limit: LIMIT,
        schoolQuery: options.schoolQuery || undefined,
        dateFrom: options.dateFrom || undefined,
        dateTo: options.dateTo || undefined,
        kind: options.kind || undefined,
        sortField: options.sortField || undefined,
        sortDirection: options.sortDirection || undefined
      }
    })
    .then(response => response.data as { count: number; items: any[] });
}

const DEFAULT_INVITE_OUTBOX_FILTER: any = {
  where: {
    kind: 'EventSchoolInvite',
    invitedSchoolId: { $exists: true, $nin: ['58611f5d4387fc25d725ddad'] }
  },
  order: 'startTime DESC'
};

const DEFAULT_INVITE_INBOX_FILTER: any = {
  where: {
    kind: 'EventSchoolInvite',
    invitedSchoolId: { $exists: true }
  },
  order: 'startTime DESC'
};

const DEFAULT_INVITE_ARCHIVE_FILTER: any = {
  where: {
    kind: 'EventSchoolInvite',
    invitedSchoolId: { $exists: true }
  },
  order: 'updatedAt DESC'
};

export function getAdminSchoolsInboxInvites(user: AppUser, filter = DEFAULT_INVITE_INBOX_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_INVITE_INBOX_FILTER.where,
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order || DEFAULT_INVITE_INBOX_FILTER.order
      }
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/invites/inbox`, config).then(response => {
    return response.data;
  });
}

export function getAdminSchoolsAllInboxInvites(user: AppUser, where = DEFAULT_INVITE_INBOX_FILTER.where) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_INVITE_INBOX_FILTER.where,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          order: DEFAULT_INVITE_INBOX_FILTER.order
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          order: DEFAULT_INVITE_INBOX_FILTER.order
        }
      }
    };
  }

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/invites/inbox`, config).then(response => {
    return response.data;
  });
}

export function getAdminSchoolsInboxInvitesCount(
  user: AppUser,
  where = DEFAULT_INVITE_INBOX_FILTER.where
): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_INVITE_INBOX_FILTER.where,
          ...where
        }
      }
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/invites/inbox/count`, config).then(response => {
    return response.data as Count;
  });
}

export function getAdminSchoolOutboxInvites(user: AppUser, filter = DEFAULT_INVITE_OUTBOX_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_INVITE_OUTBOX_FILTER.where,
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order || DEFAULT_INVITE_OUTBOX_FILTER.order
      }
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/invites/outbox`, config).then(response => {
    return response.data;
  });
}

export function getAdminSchoolsAllOutboxInvites(user: AppUser, where = DEFAULT_INVITE_OUTBOX_FILTER.where) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_INVITE_OUTBOX_FILTER.where,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          order: DEFAULT_INVITE_OUTBOX_FILTER.order
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          order: DEFAULT_INVITE_OUTBOX_FILTER.order
        }
      }
    };
  }

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/invites/outbox`, config).then(response => {
    return response.data;
  });
}

export function getAdminSchoolsOutboxInvitesCount(
  user: AppUser,
  where = DEFAULT_INVITE_OUTBOX_FILTER.where
): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_INVITE_OUTBOX_FILTER.where,
          ...where
        }
      }
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/invites/outbox/count`, config).then(response => {
    return response.data as Count;
  });
}

export function getAdminSchoolsArchiveInvites(user: AppUser, filter = DEFAULT_INVITE_ARCHIVE_FILTER) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_INVITE_ARCHIVE_FILTER.where,
          ...filter.where
        },
        skip: filter.skip,
        limit: filter.limit,
        order: filter.order || DEFAULT_INVITE_ARCHIVE_FILTER.order
      }
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/invites/archive`, config).then(response => {
    return response.data;
  });
}

export function getAdminSchoolsAllArchiveInvites(user: AppUser, where = DEFAULT_INVITE_ARCHIVE_FILTER.where) {
  const { roleSessionKey: key, activeSchoolId } = user;

  let config;

  if (typeof where !== 'undefined') {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          where: {
            ...DEFAULT_INVITE_ARCHIVE_FILTER.where,
            ...where
          },
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          order: DEFAULT_INVITE_ARCHIVE_FILTER.order
        }
      }
    };
  } else {
    config = {
      headers: { usid: key },
      params: {
        filter: {
          limit: DEFAULT_LIMIT,
          skip: DEFAULT_SKIP,
          order: DEFAULT_INVITE_ARCHIVE_FILTER.order
        }
      }
    };
  }

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/invites/archive`, config).then(response => {
    return response.data;
  });
}

export function getAdminSchoolsArchiveInvitesCount(
  user: AppUser,
  where = DEFAULT_INVITE_ARCHIVE_FILTER.where
): Promise<Count> {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key },
    params: {
      filter: {
        where: {
          ...DEFAULT_INVITE_ARCHIVE_FILTER.where,
          ...where
        }
      }
    }
  };

  return axios.get(`${window.apiBase}/i/schools/${activeSchoolId}/invites/archive/count`, config).then(response => {
    return response.data as Count;
  });
}

export function declineAdminSchoolsInvites(user: AppUser, inviteId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/invites/${inviteId}/reject`, {}, config)
    .then(response => {
      return response.data;
    });
}

export function cancelAdminSchoolsInvites(user: AppUser, inviteId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/invites/${inviteId}/cancel`, {}, config)
    .then(response => {
      return response.data;
    });
}

export function sendCommentsAdminSchoolsInvites(user: AppUser, inviteId: string, data) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .post(`${window.apiBase}/i/schools/${activeSchoolId}/invites/${inviteId}/comments`, data, config)
    .then(response => {
      return response.data;
    });
}
